import {
  type Organisation,
  SIGNS,
  type Unit,
  type UnitStatus,
  offeredOrganisation,
  signById,
} from '@orgsim/shared';
import { useEffect, useRef } from 'react';
import { ORGANISATION_LABELS, UNIT_STATUS_LABELS } from '../labels';

type Props = {
  unit: Unit;
  open: boolean;
  onOpen: () => void;
  onChange: (u: Unit) => void;
  onRemove: () => void;
};

export function UnitForm({ unit, open, onOpen, onChange, onRemove }: Props) {
  const itemRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) itemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [open]);
  const set = (patch: Partial<Unit>) => onChange({ ...unit, ...patch });
  const carriers = signById(unit.type).organisations;
  return (
    <div ref={itemRef} className={`editor-item ${open ? 'selected' : ''}`}>
      <button type="button" onClick={onOpen} aria-expanded={open} className="editor-item-header">
        <span>{unit.callsign}</span>
        <span className="text-xs text-neutral-500">
          {UNIT_STATUS_LABELS[unit.status]}
          {unit.position ? '' : ', ohne Position'}
        </span>
      </button>
      {open ? (
        <div className="space-y-2 border-t border-neutral-800 p-2">
          <label className="block">
            Typ
            <select
              value={unit.type}
              onChange={(e) =>
                set({
                  type: e.target.value,
                  organisation: offeredOrganisation(e.target.value, unit.organisation),
                })
              }
              className="mt-1 w-full rounded bg-neutral-800 px-2 py-1"
            >
              {SIGNS.filter((s) => s.isUnit).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          {carriers ? (
            <label className="block">
              Träger
              <select
                value={offeredOrganisation(unit.type, unit.organisation) ?? ''}
                onChange={(e) =>
                  set({ organisation: (e.target.value || undefined) as Organisation | undefined })
                }
                className="mt-1 w-full rounded bg-neutral-800 px-2 py-1"
              >
                <option value="">
                  Standard ({ORGANISATION_LABELS[carriers[0] ?? 'hilfsorganisation']})
                </option>
                {carriers.map((o) => (
                  <option key={o} value={o}>
                    {ORGANISATION_LABELS[o]}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label className="block">
            Funkrufname
            <input
              value={unit.callsign}
              onChange={(e) => set({ callsign: e.target.value })}
              className="mt-1 w-full rounded bg-neutral-800 px-2 py-1"
            />
          </label>
          <label className="block">
            Status
            <select
              value={unit.status}
              onChange={(e) => set({ status: e.target.value as UnitStatus })}
              className="mt-1 w-full rounded bg-neutral-800 px-2 py-1"
            >
              {(Object.keys(UNIT_STATUS_LABELS) as UnitStatus[]).map((s) => (
                <option key={s} value={s}>
                  {UNIT_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
          {unit.position ? (
            <button
              type="button"
              onClick={() => set({ position: undefined })}
              className="text-xs text-neutral-300"
            >
              Position entfernen
            </button>
          ) : null}
          <button
            type="button"
            onClick={onRemove}
            className="ml-3 text-xs text-neutral-400 hover:text-red-300"
          >
            Kraft entfernen
          </button>
        </div>
      ) : null}
    </div>
  );
}
